<template>
  <div
    v-if="isSmall"
    class="tw:border tw:border-t-0 tw:lg:border-t tw:lg:border-l-0 tw:border-gray-300 tw:flex tw:flex-row tw:items-center tw:h-auto tw:gap-x-2 tw:p-3 tw:rounded-b-lg tw:lg:rounded-b-none tw:lg:rounded-r-lg"
  >
    <p
      class="title tw:font-bold tw:inline tw:text-sm tw:items-start tw:gap-x-1"
      v-html="`${$t('rc_cpc_available_budget')}`"
    />

    <router-link
      class="tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded-lg tw:text-sm"
      :class="budgetColors"
      to="/rate-connect/running/budget"
    >
      <span class="tw:font-bold">{{ remainingBudget }}</span>
      <div class="tw:h-[21px] tw:w-[1px] tw:mx-2" :class="budgetBorder" />
      {{ !isNaN(budgetPercentage) ? formattedBudgetPercentage : '-' }}
    </router-link>
  </div>
  <Card
    v-else
    :isCustomTooltip="false"
    :title="`${$t('rc_cpc_available_budget')}: <span>${$t(currentMonth)}</span>`"
    :hint="$t('rc_budget_tooltip')"
    :isActionable="isActionable"
    type="budget"
    :hasHint="hasHint"
  >
    <template #cardData>
      <div class="tw:flex tw:items-center tw:justify-start tw:px-2 tw:py-1 tw:rounded tw:text-sm" :class="budgetColors">
        <span class="tw:font-bold">{{ remainingBudget }}</span>
        <div class="tw:h-[21px] tw:w-[1px] tw:mx-2" :class="budgetBorder" />
        {{ !isNaN(budgetPercentage) ? formattedBudgetPercentage : '-' }}
      </div>
    </template>
    <template #dropdownMenu="{ closeMenu }">
      <div
        class="tw:px-4 tw:py-3 tw:text-sm tw:flex tw:flex-col tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-t-lg"
        @click="
          redirectToPage('budgetPage', router.currentRoute.value.name === 'rate-connect-running-budget', closeMenu)
        "
        :class="{
          'tw:bg-blue-200 tw:text-blue-800 tw:cursor-default':
            router.currentRoute.value.name === 'rate-connect-running-budget',
        }"
      >
        {{ $t('notification_action_update_budget') }}
      </div>
      <div
        class="tw:px-4 tw:py-3 tw:text-sm tw:flex tw:flex-col tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-b-lg"
        @click="
          redirectToPage('biddingGoals', router.currentRoute.value.name === 'rate-connect-running-bidding', closeMenu)
        "
        :class="{
          'tw:bg-blue-200 tw:text-blue-800 tw:cursor-default':
            router.currentRoute.value.name === 'rate-connect-running-bidding',
        }"
      >
        {{ $t('rc_adjust_bidding_goal') }}
      </div>
    </template>
  </Card>
</template>
<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import Card from '@/components/rateConnect/statusBar/Card.vue'

import appAnalytics from '@/utils/tracking'

import { selectedAccommodationId } from '@/layouts/queries'
import { useScrollTo } from '@/composables/useScrollTo.ts'
import { useIsMobile } from '@/composables/useIsMobile'
import { useFormatting } from '@/composables/useFormatting'

const router = useRouter()
const props = defineProps({
  hasHint: Boolean,
  budgetPercentage: [String, Number],
  budget: Object,
  partnerStatus: String,
  campaignStatus: String,
  isActionable: Boolean,
  isSmall: {
    type: Boolean,
    default: false,
  },
})
const { formatCurrency, formatPercentage } = useFormatting()
const months = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

const { scrollTo } = useScrollTo()
const { isMobile } = useIsMobile()
const trackEvent = option => {
  appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_MENU_OPTION_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    cardType: 'budget',
    option,
    campaignType: 'cpc',
  })
}

const redirectToPage = (option, isSelected, closeMenu) => {
  if (!isSelected) {
    trackEvent(option)
    if (isMobile.value) {
      scrollTo('rcNav')
    }
    if (option === 'budgetPage') {
      router.push({ name: 'rate-connect-running-budget' })
    }
    if (option === 'biddingGoals') {
      router.push({ name: 'rate-connect-running-bidding' })
    }
    closeMenu()
  }
}

const currentMonth = computed(() => {
  const currentDate = new Date()
  const monthIndex = currentDate.getMonth()
  return months[monthIndex]
})

const formattedBudgetPercentage = computed(() => formatPercentage(props.budgetPercentage, 0, 0))

const budgetColors = computed(() => {
  if (props.partnerStatus !== 'active' || props.partnerStatus !== 'active') return 'tw:bg-gray-200 tw:text-gray-700'
  if (props.budgetPercentage <= 0 || isNaN(props.budgetPercentage)) return 'tw:bg-red-200 tw:text-red-700'
  else if (props.budgetPercentage > 0 && props.budgetPercentage < 30) return 'tw:bg-orange-200 tw:text-orange-700'
  else return 'tw:bg-green-200 tw:text-green-700'
})
const budgetBorder = computed(() => {
  if (props.partnerStatus !== 'active' || props.partnerStatus !== 'active') return 'tw:bg-gray-300'
  if (props.budgetPercentage <= 0 || isNaN(props.budgetPercentage)) return 'tw:bg-red-300'
  else if (props.budgetPercentage > 0 && props.budgetPercentage < 30) return 'tw:bg-orange-300'
  else return 'tw:bg-green-300'
})

const remainingBudget = computed(() => formatCurrency(props.budget?.left))
</script>
